/**
 * Created by alvinoktavianus on 6/27/17.
 */

import { call, put, takeLatest } from 'redux-saga/effects'
import * as action from 'actions'
import * as actNotif from 'actions/notifications'
import _ from 'lodash'
import moment from 'moment'
import {
  disablePayButton,
  enablePayButton,
  featurePrice,
  featurePricePerMonths,
  finishLoadingPage,
  getBillings,
  getOutlets,
  setCurrentOutletCount,
  setFeaturePrice,
  setFeaturePricePerMonths,
  setIsOnTutorial,
  setOutlets,
  setPayButtonMessage,
  setPendingOutletMessage,
  setPendingPaymentMessage,
  setSubscriptionDetail,
  showPaymentGatewayErrorModal,
  subscriptionStatus
} from 'actions/ingredients/ingredientSubscription'
import { getFeaturePricePerMonths } from 'api/feature/getFeaturePricePerMonths'
import { getFeaturePrice } from 'api/feature/getFeaturePrice'
import { getSubscriptionStatus } from 'api/feature/getSubscriptionStatus'
import { endTutorial } from 'api/feature/endTutorial'
import { startTrial } from 'api/feature/startTrial'
import * as apiBilling from 'api/billing'
import {
  INGREDIENT_SUBSCRIPTION_END_TUTORIAL,
  INGREDIENT_SUBSCRIPTION_FEATURE_PRICE,
  INGREDIENT_SUBSCRIPTION_FEATURE_PRICE_PER_MONTHS,
  INGREDIENT_SUBSCRIPTION_GET_BILLINGS,
  INGREDIENT_SUBSCRIPTION_GET_OUTLETS,
  INGREDIENT_SUBSCRIPTION_PAGE_REQUEST,
  INGREDIENT_SUBSCRIPTION_START_TRIAL,
  INGREDIENT_SUBSCRIPTION_SUBSCRIPTION_STATUS,
  INGREDIENT_SUBSCRIPTION_VERIFY_BILLING
} from 'actions/ingredients/ingredientSubscription/constant'
import { showBillingNotification } from 'actions/billingNotification/billingNotification'
import { setNotificationMessage, setNotificationType } from 'actions/billingRedirect/index'
import * as is from 'is_js'

export function * getBillingsFlow () {
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())

  try {
    const response = yield call(apiBilling.getBillings)
    let pendingPaymentMessage = null
    _.forEach(response.data, (invoice) => {
      if (invoice.status === 'pending') {
        if (invoice.payment_type === 'credit_card') {
          pendingPaymentMessage = `There is a pending payment for ${invoice.invoice_number}. Please wait 24 hours before making a new payment.`
        } else if (invoice.payment_type === 'bank_transfer' || invoice.payment_type === 'echannel') {
          pendingPaymentMessage = `There is a pending payment for ${invoice.invoice_number}. Please transfer full amount of the invoice within 3 days before making a new payment.`
        }
        return false
      }
    })
    if (pendingPaymentMessage !== null) {
      yield put(setPendingPaymentMessage(pendingPaymentMessage))
      yield put(disablePayButton())
    }
  } catch (error) {

  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
  }
}

export function * pageRequestFlow (request) {
  const {trialEndAt, outletSlot, billingNotification, alreadySubscribed} = request.payload
  yield put(featurePricePerMonths())
  yield put(featurePrice(trialEndAt, outletSlot, alreadySubscribed))
  yield put(getBillings())
  yield put(getOutlets())
  yield put(showBillingNotification(billingNotification))
  yield put(subscriptionStatus())
  yield put(setNotificationType(null))
  yield put(setNotificationMessage(null))
}

export function * getFeaturePricePerMonthsFlow () {
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())

  try {
    const response = yield call(getFeaturePricePerMonths, {feature_names: ['Ingredient Inventory']})
    yield put(setFeaturePricePerMonths(response.data['Ingredient Inventory']))
  } catch (error) {

  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
  }
}

export function * getSubscriptionStatusFlow () {
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())

  try {
    const response = yield call(getSubscriptionStatus, {feature_names: ['Ingredient Inventory']})
    yield put(setIsOnTutorial(response.data[0].on_tutorial))
    yield put(setSubscriptionDetail(response.data[0]))
  } catch (error) {

  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
  }

  // We must put this action at the end of page request action so there will not be a blink in ingredient
  yield put(finishLoadingPage())
}

export function * getFeaturePriceFlow (request) {
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())

  try {
    const response = yield call(getFeaturePrice, {feature_name: 'Ingredient Inventory'})
    const {trialEndAt, outletSlot, alreadySubscribed} = request.payload
    let subscriptionPriceDetail = {}
    let currentOutlet = 0
    const today = new Date().getTime()
    const trialEndDate = new Date(moment(trialEndAt).format('YYYY-MM-DD')).getTime()

    _.map(response.data, (value, key) => {
      if (key !== 'final_price' && value.final_price !== 0) {
        currentOutlet++
        subscriptionPriceDetail[key] = {}
        subscriptionPriceDetail[key]['start_date'] = today < trialEndDate ? new Date() : value.start_date
        subscriptionPriceDetail[key]['final_price'] = value.final_price
        subscriptionPriceDetail[key]['end_date'] = value.end_date
      } else {
        subscriptionPriceDetail[key] = value
      }
    })

    let outletLeft = !alreadySubscribed ? 0 : outletSlot - currentOutlet
    yield put(setCurrentOutletCount(currentOutlet))
    if (outletLeft > 0) {
      yield put(setPendingOutletMessage(`You still have slots for additional ${outletLeft} ${outletLeft === 1 ? 'outlet' : 'outlets'}. Please create the outlets before making a payment.`))
      yield put(disablePayButton())
    }

    yield put(setFeaturePrice(subscriptionPriceDetail))
  } catch (error) {

  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
  }
}

export function * endTutorialFlow (request) {
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())

  try {
    yield call(endTutorial, request.payload.featureName)
    window.location.reload()
  } catch (error) {
    yield put(actNotif.error('Please try again'))
  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
  }
}

export function * getOutletsFlow () {
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())

  try {
    const response = yield call(apiBilling.getAllOutlets)
    yield put(setOutlets(response.data))
  } catch (error) {

  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
  }
}

export function * verifyBillingFlow (request) {
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())
  yield put(disablePayButton())

  let response = {}

  try {
    response = yield call(apiBilling.verifyBilling, request.payload.billingData)
  } catch (error) {
    yield put(enablePayButton())
    if (!error.data || error.data === ' ' || error.status >= 500) {
      yield put(showPaymentGatewayErrorModal())
    } else {
      yield put(actNotif.error(error.data.message))
    }
  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
    if (response.data) {
      yield put(setPayButtonMessage('Redirecting to Veritrans'))
      if (is.safari()) {
        yield put(enablePayButton())
        yield put(setPayButtonMessage('Pay'))
      }
      window.location.href = response.data.data.redirect_url
    }
  }
}

export function * startTrialFlow () {
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())

  try {
    yield call(startTrial, {feature_name: 'Ingredient Inventory'})
    yield put(actNotif.success('Ingredient Inventory feature is unlocked.'))
    yield put(setIsOnTutorial(true))
  } catch (error) {
    yield put(actNotif.error('Please try again later.'))
  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
  }
}

export default function * saga () {
  yield takeLatest(INGREDIENT_SUBSCRIPTION_PAGE_REQUEST, pageRequestFlow)
  yield takeLatest(INGREDIENT_SUBSCRIPTION_FEATURE_PRICE_PER_MONTHS, getFeaturePricePerMonthsFlow)
  yield takeLatest(INGREDIENT_SUBSCRIPTION_FEATURE_PRICE, getFeaturePriceFlow)
  yield takeLatest(INGREDIENT_SUBSCRIPTION_GET_BILLINGS, getBillingsFlow)
  yield takeLatest(INGREDIENT_SUBSCRIPTION_SUBSCRIPTION_STATUS, getSubscriptionStatusFlow)
  yield takeLatest(INGREDIENT_SUBSCRIPTION_END_TUTORIAL, endTutorialFlow)
  yield takeLatest(INGREDIENT_SUBSCRIPTION_GET_OUTLETS, getOutletsFlow)
  yield takeLatest(INGREDIENT_SUBSCRIPTION_VERIFY_BILLING, verifyBillingFlow)
  yield takeLatest(INGREDIENT_SUBSCRIPTION_START_TRIAL, startTrialFlow)
}



// WEBPACK FOOTER //
// ./app/sagas/ingredients/ingredientSubscription.js